import { styled, SupersetClient, t } from '@superset-ui/core';
import { uniq, sortBy } from 'lodash';
import { FunctionComponent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Button from 'src/components/Button';
import ModalTrigger from 'src/components/ModalTrigger';
import { useToasts } from 'src/components/MessageToasts/withToasts';

interface LinkDashboardModalProps {
  dashboard: number;
}

interface Package {
  name: string;
  id: string;
  is_maintainer: boolean;
  is_public_linked: boolean;
  is_user_linked: boolean;
}

const RightAlign = styled.div`
  display: inline;
  margin-left: auto;
`;
const Row = styled.div`
  padding-top: 10px;
  display: flex;
`;

const LinkDashboardModal: FunctionComponent<LinkDashboardModalProps> = ({
  dashboard,
}) => {
  const { addSuccessToast, addDangerToast } = useToasts();
  const CKAN_URL = useSelector<{ common: { CKAN_URL: string } }, string>(
    s => s.common.CKAN_URL,
  );
  const userIsAnonymous = useSelector<
    { user: { isAnonymous?: boolean } },
    boolean
  >(s => s.user.isAnonymous ?? true);
  const [packages, setPackages] = useState<{ [id: string]: Package }>({});
  useEffect(() => {
    (async () => {
      const datasetsResponse = await SupersetClient.get({
        endpoint: `/api/v1/dashboard/${dashboard}/datasets`,
      });
      const datasets: string[] = datasetsResponse.json.result.map(
        (p: { table_name: string }) => p.table_name,
      );
      const resourceShows = await Promise.all(
        datasets.map(table_name =>
          SupersetClient.get({
            url: `${CKAN_URL}/api/3/action/resource_show?id=${table_name}`,
          }).catch(e => {
            console.error(e);
            return null;
          }),
        ),
      );
      const packages: string[] = uniq(
        resourceShows.filter(p => p).map(p => p!.json.result.package_id),
      );
      const packageResponses = await Promise.all(
        packages.map(p =>
          SupersetClient.get({
            url: `${CKAN_URL}/api/3/action/package_dashboard_data?package=${p}`,
          }),
        ),
      );
      const packageData: Package[] = packageResponses.map(
        ({
          json: {
            result: { name, id, dashboards, user_dashboards, is_maintainer },
          },
        }) => ({
          name,
          id,
          is_maintainer,
          is_public_linked: dashboards.includes(dashboard),
          is_user_linked: user_dashboards?.includes(dashboard) ?? false,
        }),
      );
      setPackages(Object.fromEntries(packageData.map(p => [p.id, p])));
    })().catch(e => {
      console.error(e);
    });
  }, [dashboard]);

  const linker =
    ({
      success,
      failure,
      scope,
      action,
    }: {
      success: string;
      failure: string;
      scope: string;
      action: string;
    }) =>
    (package_id: string) =>
    () =>
      SupersetClient.post({
        url: `${CKAN_URL}/api/3/action/${scope}_${action}_dashboard`,
        jsonPayload: {
          package: package_id,
          dashboard,
        },
      })
        .then(() => {
          addSuccessToast(t(success, packages[package_id].name));
          setPackages(({ [package_id]: current, ...rest }) => ({
            ...rest,
            [package_id]: {
              ...current,
              [`is_${scope}_linked`]: action === 'add',
            },
          }));
        })
        .catch(e => {
          console.error(e);
          addDangerToast(t(failure, packages[package_id].name));
        });

  const linkers = {
    user: {
      link: linker({
        success: 'Linked dashboard to dataset %s for you',
        failure: 'Failed to link dashboard to dataset %s for you',
        action: 'add',
        scope: 'user',
      }),
      unlink: linker({
        success: 'Unlinked dashboard from dataset %s for you',
        failure: 'Failed to unlink dashboard from dataset %s for you',
        action: 'rm',
        scope: 'user',
      }),
    },
    public: {
      link: linker({
        success: 'Linked dashboard to dataset %s for the public',
        failure: 'Failed to link dashboard to dataset %s for the public',
        action: 'add',
        scope: 'public',
      }),
      unlink: linker({
        success: 'Unlinked dashboard from dataset %s for the public',
        failure: 'Failed to unlink dashboard from dataset %s for the public',
        action: 'rm',
        scope: 'public',
      }),
    },
  };

  return (
    <ModalTrigger
      responsive
      modalTitle={t('Datasets')}
      modalBody={sortBy(Object.values(packages), 'name').map(
        ({ name, id, is_maintainer, is_public_linked, is_user_linked }) => (
          <Row key={id}>
            <a href={`${CKAN_URL}/dataset/${id}`}>{name}</a>
            <RightAlign>
              {is_maintainer && (
                <Button
                  css={{
                    width: '12em',
                  }}
                  onClick={
                    is_public_linked
                      ? linkers.public.unlink(id)
                      : linkers.public.link(id)
                  }
                >
                  {
                    // eslint-disable-next-line translation-vars/no-template-vars -- Constant set of strings to be used
                    t(`${is_public_linked ? 'Unlink' : 'Link'} (Public)`)
                  }
                </Button>
              )}
              {!userIsAnonymous && (
                <Button
                  css={{
                    width: '12em',
                  }}
                  onClick={
                    is_user_linked
                      ? linkers.user.unlink(id)
                      : linkers.user.link(id)
                  }
                >
                  {
                    // eslint-disable-next-line translation-vars/no-template-vars -- Constant set of strings to be used
                    t(`${is_user_linked ? 'Unlink' : 'Link'} (User)`)
                  }
                </Button>
              )}
            </RightAlign>
          </Row>
        ),
      )}
      triggerNode={t('Datasets')}
    />
  );
};

export default LinkDashboardModal;
